import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { BrandLogoComponent } from '../brand-logo.component';
import { BrandListRow } from './brand-list-row.model';

/**
 * AG-Grid cell renderer for the brand logo column. Wraps BrandLogoComponent so
 * the logo URL goes through Angular's property-binding sanitiser instead of being
 * interpolated into a raw HTML string (which would expose an XSS surface if the
 * brand domain contained an attribute-breaking character).
 *
 * Brand rows render the brand logo (from the brand domain); sub-brand rows render
 * the sub-brand logo (sub-folder of the parent brand's asset domain). Either drops
 * its `<img>` on 404 (no fallback, AD-16).
 */
@Component({
  selector: 'cmt-brand-logo-cell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BrandLogoComponent],
  template: `
    @if (logoDomain(); as domain) {
      <cmt-brand-logo [domain]="domain" [subBrand]="subBrand()" variant="thumb" />
    }
  `,
  styles: [`:host { display: flex; align-items: center; justify-content: center; height: 100%; }`],
})
export class BrandLogoCellComponent implements ICellRendererAngularComp {
  /** Brand: its own asset domain. Sub-brand: the parent brand's domain. */
  protected readonly logoDomain = signal<string | null>(null);
  /** Set only for sub-brand rows — selects the sub-folder under the parent domain. */
  protected readonly subBrand = signal<string | null>(null);

  private apply(data: BrandListRow | undefined): void {
    if (data?.type === 'brand') {
      this.logoDomain.set(data.logoDomain ?? null);
      this.subBrand.set(null);
    } else if (data?.type === 'subbrand') {
      this.logoDomain.set(data.parentDomain ?? null);
      this.subBrand.set(data.name ?? null);
    } else {
      this.logoDomain.set(null);
      this.subBrand.set(null);
    }
  }

  agInit(params: ICellRendererParams<BrandListRow>): void {
    this.apply(params.data ?? undefined);
  }

  refresh(params: ICellRendererParams<BrandListRow>): boolean {
    this.apply(params.data ?? undefined);
    return true;
  }
}
